import {IntlShape, MessageDescriptor, defineMessages} from 'react-intl'

/**
 * Catálogo de Organizaciones (`/organizations`, `/organization/:id`).
 * Namespace `admin.organizations.*` (Fase 1.11).
 *
 * No se traducen: el nombre, el propietario, la web y la dirección de una
 * organización (datos de BD), los nombres de los formularios de firma, la clave
 * de parámetro de sistema `enforce_us_location_restriction` (identificador de
 * configuración) ni los mensajes de error del backend, que llegan dentro de
 * `common.error.withMessage`.
 */
export const ORG_MESSAGES = defineMessages({
  // ── Listado ──
  myOrganization: {id: 'admin.organizations.my.title', defaultMessage: 'My Organization'},
  myOrganizationHint: {
    id: 'admin.organizations.my.hint',
    defaultMessage: "Manage your organization's details",
  },
  editOrganization: {
    id: 'admin.organizations.action.edit',
    defaultMessage: 'Edit Organization',
  },
  noOrganizationForAccount: {
    id: 'admin.organizations.my.none',
    defaultMessage: 'No organization found for your account.',
  },
  listTitle: {id: 'admin.organizations.list.title', defaultMessage: 'Organizations'},
  listHint: {
    id: 'admin.organizations.list.hint',
    defaultMessage: 'Manage all organizations in the platform',
  },
  activeCount: {id: 'admin.organizations.list.activeCount', defaultMessage: 'Active ({count})'},
  archivedCount: {
    id: 'admin.organizations.list.archivedCount',
    defaultMessage: 'Archived ({count})',
  },
  newOrganization: {id: 'admin.organizations.action.new', defaultMessage: 'New Organization'},
  searchPlaceholder: {
    id: 'admin.organizations.list.search',
    defaultMessage: 'Search by name, owner, or email...',
  },
  thLocation: {id: 'common.field.location', defaultMessage: 'Location'},
  emptyArchived: {
    id: 'admin.organizations.list.emptyArchived',
    defaultMessage: 'No archived organizations',
  },
  emptyActive: {
    id: 'admin.organizations.list.emptyActive',
    defaultMessage: 'No organizations found',
  },
  archive: {id: 'admin.organizations.action.archive', defaultMessage: 'Archive'},
  createOrganization: {
    id: 'admin.organizations.action.create',
    defaultMessage: 'Create Organization',
  },

  // ── Archivar (dos pasos) ──
  archiveTitle: {
    id: 'admin.organizations.archive.title',
    defaultMessage: 'Archive Organization',
  },
  archiveConfirm: {
    id: 'admin.organizations.archive.confirm',
    defaultMessage: 'Are you sure you want to archive <b>"{name}"</b>?',
  },
  archiveWhatHappens: {
    id: 'admin.organizations.archive.whatHappens',
    defaultMessage: 'What happens when you archive:',
  },
  archiveEffectList: {
    id: 'admin.organizations.archive.effect.list',
    defaultMessage: 'The organization will no longer appear in active lists',
  },
  archiveEffectAccess: {
    id: 'admin.organizations.archive.effect.access',
    defaultMessage: 'Users assigned to this organization may lose access',
  },
  archiveEffectConfig: {
    id: 'admin.organizations.archive.effect.config',
    defaultMessage: 'All associated configurations will be disabled',
  },
  archiveConfirmTitle: {
    id: 'admin.organizations.archive.confirmTitle',
    defaultMessage: 'Confirm Archive',
  },
  archiveIrreversibleTitle: {
    id: 'admin.organizations.archive.irreversibleTitle',
    defaultMessage: 'This action cannot be undone',
  },
  archiveIrreversibleBody: {
    id: 'admin.organizations.archive.irreversibleBody',
    defaultMessage:
      'You are about to permanently archive <b>"{name}"</b>. This action is irreversible and cannot be reverted.',
  },
  archiving: {id: 'admin.organizations.archive.inProgress', defaultMessage: 'Archiving...'},
  archivePermanently: {
    id: 'admin.organizations.archive.permanently',
    defaultMessage: 'Archive Permanently',
  },
  toastArchived: {
    id: 'admin.organizations.toast.archived',
    defaultMessage: 'Organization archived successfully',
  },
  errArchiving: {
    id: 'admin.organizations.error.archiving',
    defaultMessage: 'Error archiving organization',
  },

  // ── Ficha: pestañas ──
  tabGeneral: {id: 'admin.organizations.tab.general', defaultMessage: 'General'},
  tabEnvironment: {
    id: 'admin.organizations.tab.environment',
    defaultMessage: 'Environment Setup',
  },
  tabChannelsConfig: {
    id: 'admin.organizations.tab.channelsConfig',
    defaultMessage: 'Channels Config',
  },
  tabChannelsList: {
    id: 'admin.organizations.tab.channelsList',
    defaultMessage: 'Organization Channels',
  },
  tabOtherSettings: {
    id: 'admin.organizations.tab.otherSettings',
    defaultMessage: 'Other Settings',
  },
  tabEmailConfigurations: {
    id: 'admin.organizations.tab.emailConfigurations',
    defaultMessage: 'Email Configurations',
  },
  tabOnboarding: {id: 'admin.organizations.tab.onboarding', defaultMessage: 'Onboarding'},
  subTabEmailSetup: {id: 'admin.organizations.tab.emailSetup', defaultMessage: 'Email Setup'},
  subTabEmailSettings: {
    id: 'admin.organizations.tab.emailSettings',
    defaultMessage: 'Email Settings',
  },
  notFound: {
    id: 'admin.organizations.notFound',
    defaultMessage: "Organization not found, or you don't have access to it.",
  },
  systemParameters: {
    id: 'admin.organizations.systemParameters',
    defaultMessage: 'System Parameters',
  },

  // ── Formulario general ──
  organizationLogo: {id: 'admin.organizations.logo.title', defaultMessage: 'Organization Logo'},
  logoHint: {id: 'admin.organizations.logo.hint', defaultMessage: 'JPG, PNG or WebP. Max 2MB.'},
  templateLogo: {id: 'admin.organizations.templateLogo.title', defaultMessage: 'Template Logo'},
  templateLogoHint: {
    id: 'admin.organizations.templateLogo.hint',
    defaultMessage: 'For document templates.',
  },
  logoAlt: {id: 'admin.organizations.logo.alt', defaultMessage: 'Logo'},
  templateLogoAlt: {id: 'admin.organizations.templateLogo.title', defaultMessage: 'Template Logo'},
  change: {id: 'admin.organizations.action.change', defaultMessage: 'Change'},
  upload: {id: 'admin.organizations.action.upload', defaultMessage: 'Upload'},
  fieldOwner: {id: 'admin.organizations.field.owner', defaultMessage: 'Owner'},
  fieldWebsite: {id: 'admin.organizations.field.website', defaultMessage: 'Website'},
  fieldPostalCode: {id: 'admin.organizations.field.postalCode', defaultMessage: 'Postal Code'},
  placeholderName: {
    id: 'admin.organizations.placeholder.name',
    defaultMessage: 'Organization name',
  },
  placeholderOwner: {
    id: 'admin.organizations.placeholder.owner',
    defaultMessage: 'Agency owner name',
  },
  placeholderAddress: {
    id: 'admin.organizations.placeholder.address',
    defaultMessage: 'Street address',
  },
  placeholderCity: {id: 'common.field.city', defaultMessage: 'City'},
  placeholderCountry: {id: 'admin.organizations.placeholder.country', defaultMessage: 'Country'},

  // ── Toasts del formulario ──
  errRequiredFields: {
    id: 'admin.organizations.error.requiredFields',
    defaultMessage: 'Please fill all required fields (Name, Owner, Email, Phone)',
  },
  errValidation: {
    id: 'admin.organizations.error.validation',
    defaultMessage: 'Validation errors: {messages}',
  },
  toastSaved: {
    id: 'admin.organizations.toast.saved',
    defaultMessage: 'Organization updated successfully',
  },
  toastCreated: {
    id: 'admin.organizations.toast.created',
    defaultMessage: 'Organization created successfully',
  },
  errSaving: {
    id: 'admin.organizations.error.saving',
    defaultMessage: 'Error saving organization',
  },
  toastLogoUpdated: {
    id: 'admin.organizations.toast.logoUpdated',
    defaultMessage: 'Logo updated successfully',
  },
  errLogo: {id: 'admin.organizations.error.logo', defaultMessage: 'Logo error: {message}'},
  errUploadingLogo: {
    id: 'admin.organizations.error.uploadingLogo',
    defaultMessage: 'Error uploading logo',
  },
  toastTplLogoUpdated: {
    id: 'admin.organizations.toast.tplLogoUpdated',
    defaultMessage: 'Template logo updated successfully',
  },
  errTplLogo: {
    id: 'admin.organizations.error.tplLogo',
    defaultMessage: 'Template logo error: {message}',
  },
  errUploadingTplLogo: {
    id: 'admin.organizations.error.uploadingTplLogo',
    defaultMessage: 'Error uploading template logo',
  },
  unknownError: {id: 'common.error.unknown', defaultMessage: 'Unknown error'},

  // ── Parámetros de sistema ──
  settingsHint: {
    id: 'admin.organizations.settings.hint',
    defaultMessage: 'System parameters scoped to this organization (e.g. <code>{example}</code>).',
  },
  addSetting: {id: 'admin.organizations.settings.add', defaultMessage: 'Add Setting'},
  thValue: {id: 'admin.organizations.th.value', defaultMessage: 'Value'},
  settingsEmpty: {
    id: 'admin.organizations.settings.empty',
    defaultMessage: 'No settings for this organization yet.',
  },
  placeholderSettingName: {
    id: 'admin.organizations.settings.placeholder.name',
    defaultMessage: 'setting_name',
  },
  placeholderSettingValue: {
    id: 'admin.organizations.settings.placeholder.value',
    defaultMessage: 'value',
  },
  fillNameAndValue: {
    id: 'admin.organizations.settings.fillBoth',
    defaultMessage: 'Please fill both name and value',
  },
  toastSettingCreated: {
    id: 'admin.organizations.settings.toast.created',
    defaultMessage: 'Setting created',
  },
  errSettingCreate: {
    id: 'admin.organizations.settings.error.create',
    defaultMessage: 'Error creating setting',
  },
  toastSettingUpdated: {
    id: 'admin.organizations.settings.toast.updated',
    defaultMessage: 'Setting updated',
  },
  errSettingUpdate: {
    id: 'admin.organizations.settings.error.update',
    defaultMessage: 'Error updating setting',
  },
  confirmDeleteSetting: {
    id: 'admin.organizations.settings.confirmDelete',
    defaultMessage: 'Delete this setting?',
  },
  toastSettingDeleted: {
    id: 'admin.organizations.settings.toast.deleted',
    defaultMessage: 'Setting deleted',
  },
  errSettingDelete: {
    id: 'admin.organizations.settings.error.delete',
    defaultMessage: 'Error deleting setting',
  },

  // ── Onboarding ──
  stepMessages: {id: 'admin.organizations.onboarding.stepMessages', defaultMessage: 'Step Messages'},
  requiredDocuments: {
    id: 'admin.organizations.onboarding.requiredDocuments',
    defaultMessage: 'Required Documents',
  },
  agreements: {
    id: 'admin.organizations.onboarding.agreements',
    defaultMessage: 'Agreements/Consents',
  },
  addStepMessage: {
    id: 'admin.organizations.onboarding.addStepMessage',
    defaultMessage: 'Add Step Message',
  },
  thStep: {id: 'admin.organizations.onboarding.th.step', defaultMessage: 'Step'},
  thMessage: {id: 'common.field.message', defaultMessage: 'Message'},
  placeholderMessage: {
    id: 'admin.organizations.onboarding.placeholder.message',
    defaultMessage: 'message',
  },
  stepMessagesEmpty: {
    id: 'admin.organizations.onboarding.stepMessages.empty',
    defaultMessage: 'No step messages configured for this organization yet.',
  },
  addRequiredDocument: {
    id: 'admin.organizations.onboarding.addRequiredDocument',
    defaultMessage: 'Add Required Document',
  },
  thDocumentType: {
    id: 'admin.organizations.onboarding.th.documentType',
    defaultMessage: 'Document Type',
  },
  thContext: {id: 'admin.organizations.onboarding.th.context', defaultMessage: 'Context'},
  thRequired: {id: 'admin.organizations.onboarding.th.required', defaultMessage: 'Required'},
  documentsEmpty: {
    id: 'admin.organizations.onboarding.documents.empty',
    defaultMessage: 'No required documents configured for this organization yet.',
  },
  agreementsHint: {
    id: 'admin.organizations.onboarding.agreements.hint',
    defaultMessage:
      'Select which signature forms (agreements/consents) this organization requires during onboarding.',
  },
  addConsent: {
    id: 'admin.organizations.onboarding.addConsent',
    defaultMessage: 'Add Consent',
  },
  noSignatureForms: {
    id: 'admin.organizations.onboarding.noForms',
    defaultMessage: 'No signature forms available for this organization.',
  },
  toastStepCreated: {
    id: 'admin.organizations.onboarding.toast.stepCreated',
    defaultMessage: 'Step message created',
  },
  errStepCreate: {
    id: 'admin.organizations.onboarding.error.stepCreate',
    defaultMessage: 'Error creating step message',
  },
  toastStepUpdated: {
    id: 'admin.organizations.onboarding.toast.stepUpdated',
    defaultMessage: 'Step message updated',
  },
  errStepUpdate: {
    id: 'admin.organizations.onboarding.error.stepUpdate',
    defaultMessage: 'Error updating step message',
  },
  confirmDeleteStep: {
    id: 'admin.organizations.onboarding.confirmDeleteStep',
    defaultMessage: 'Delete this step message?',
  },
  toastStepDeleted: {
    id: 'admin.organizations.onboarding.toast.stepDeleted',
    defaultMessage: 'Step message deleted',
  },
  errStepDelete: {
    id: 'admin.organizations.onboarding.error.stepDelete',
    defaultMessage: 'Error deleting step message',
  },
  toastDocCreated: {
    id: 'admin.organizations.onboarding.toast.docCreated',
    defaultMessage: 'Required document created',
  },
  errDocCreate: {
    id: 'admin.organizations.onboarding.error.docCreate',
    defaultMessage: 'Error creating required document',
  },
  toastDocUpdated: {
    id: 'admin.organizations.onboarding.toast.docUpdated',
    defaultMessage: 'Required document updated',
  },
  errDocUpdate: {
    id: 'admin.organizations.onboarding.error.docUpdate',
    defaultMessage: 'Error updating required document',
  },
  confirmDeleteDoc: {
    id: 'admin.organizations.onboarding.confirmDeleteDoc',
    defaultMessage: 'Delete this required document config?',
  },
  toastDocDeleted: {
    id: 'admin.organizations.onboarding.toast.docDeleted',
    defaultMessage: 'Required document deleted',
  },
  errDocDelete: {
    id: 'admin.organizations.onboarding.error.docDelete',
    defaultMessage: 'Error deleting required document',
  },
  toastAgreementsUpdated: {
    id: 'admin.organizations.onboarding.toast.agreementsUpdated',
    defaultMessage: 'Agreements updated',
  },
  errAgreementsUpdate: {
    id: 'admin.organizations.onboarding.error.agreementsUpdate',
    defaultMessage: 'Error updating agreements',
  },

  // ── Correo de la organización ──
  emailSettingsHint: {
    id: 'admin.organizations.email.settingsHint',
    defaultMessage:
      "Sender and signature for emails sent from this organization. Fields without an override inherit the system's default configuration.",
  },
  emailSetupHint: {
    id: 'admin.organizations.email.setupHint',
    defaultMessage:
      "SMTP transport for this organization's emails. Fields without override inherit the system default configuration (Policytools Settings → Email Setup).",
  },
  errTestEmail: {
    id: 'admin.organizations.email.error.test',
    defaultMessage: 'Error sending test email',
  },
  toastTestEmailSent: {
    id: 'admin.organizations.email.toast.testSent',
    defaultMessage: 'Test email sent to {email}',
  },
  errSavingEmailSettings: {
    id: 'admin.organizations.email.error.savingSettings',
    defaultMessage: 'Error saving Email Settings',
  },
  toastEmailSettingsSaved: {
    id: 'admin.organizations.email.toast.settingsSaved',
    defaultMessage: 'Email Settings saved',
  },
  errSavingEmailSetup: {
    id: 'admin.organizations.email.error.savingSetup',
    defaultMessage: 'Error saving Email Setup',
  },
  toastEmailSetupSaved: {
    id: 'admin.organizations.email.toast.setupSaved',
    defaultMessage: 'Email Setup saved',
  },

  // ── Reusadas del catálogo común ──
  name: {id: 'common.field.name', defaultMessage: 'Name'},
  email: {id: 'common.field.email', defaultMessage: 'Email'},
  phone: {id: 'common.field.phone', defaultMessage: 'Phone'},
  address: {id: 'common.field.address', defaultMessage: 'Address'},
  city: {id: 'common.field.city', defaultMessage: 'City'},
  state: {id: 'common.field.state', defaultMessage: 'State'},
  actions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  edit: {id: 'common.action.edit', defaultMessage: 'Edit'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  save: {id: 'common.action.save', defaultMessage: 'Save'},
  saveChanges: {id: 'common.action.saveChanges', defaultMessage: 'Save changes'},
  saving: {id: 'common.state.saving', defaultMessage: 'Saving...'},
  continueAction: {id: 'common.action.continue', defaultMessage: 'Continue'},
  yes: {id: 'common.boolean.yes', defaultMessage: 'Yes'},
  no: {id: 'common.boolean.no', defaultMessage: 'No'},
  active: {id: 'common.status.active', defaultMessage: 'Active'},
  errorWithMessage: {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
})

/** Inicial de respaldo cuando la organización no tiene logo. Símbolo. */
export const INITIAL_FALLBACK = '?'
/** Guion largo de celda vacía. Símbolo, no texto. */
export const EM_DASH = '—'
/** Ejemplo de clave de parámetro: identificador de configuración, no texto. */
export const SETTING_KEY_EXAMPLE = 'enforce_us_location_restriction'

type Enum = Record<string, MessageDescriptor>

/** Refleja `STEP_KEYS` del panel de onboarding. */
const STEP_KEY: Enum = defineMessages({
  personal_info: {
    id: 'admin.organizations.onboarding.step.personalInfo',
    defaultMessage: 'Personal Information',
  },
  licensing: {
    id: 'admin.organizations.onboarding.step.licensing',
    defaultMessage: 'Licensing',
  },
  professional_info: {
    id: 'admin.organizations.onboarding.step.professionalInfo',
    defaultMessage: 'Professional Information',
  },
}) as Enum

/** Refleja `DOCUMENT_TYPES`. `W-9` y `E&O` son nombres de formulario de EE. UU. */
const DOCUMENT_TYPE: Enum = defineMessages({
  governmentId: {
    id: 'admin.organizations.onboarding.doc.governmentId',
    defaultMessage: 'Government ID',
  },
  insuranceLicense: {
    id: 'admin.organizations.onboarding.doc.insuranceLicense',
    defaultMessage: 'Insurance License',
  },
  w9: {id: 'admin.organizations.onboarding.doc.w9', defaultMessage: 'W-9'},
  eoCertificate: {
    id: 'admin.organizations.onboarding.doc.eoCertificate',
    defaultMessage: 'E&O Certificate',
  },
  bankDocumentation: {
    id: 'admin.organizations.onboarding.doc.bankDocumentation',
    defaultMessage: 'Bank Documentation',
  },
  agencyDocumentation: {
    id: 'admin.organizations.onboarding.doc.agencyDocumentation',
    defaultMessage: 'Agency Documentation',
  },
}) as Enum

/** Refleja `DOCUMENT_CONTEXTS`. */
const DOCUMENT_CONTEXT: Enum = defineMessages({
  licensed_yes: {
    id: 'admin.organizations.onboarding.ctx.licensedYes',
    defaultMessage: 'Licensed - Yes',
  },
  licensed_in_process: {
    id: 'admin.organizations.onboarding.ctx.licensedInProcess',
    defaultMessage: 'Licensed - In Process',
  },
  agency: {id: 'admin.organizations.onboarding.ctx.agency', defaultMessage: 'Agency'},
  individual: {
    id: 'admin.organizations.onboarding.ctx.individual',
    defaultMessage: 'Individual',
  },
  general: {id: 'admin.organizations.onboarding.ctx.general', defaultMessage: 'General'},
}) as Enum

const label = (intl: IntlShape, messages: Enum, value: string) =>
  messages[value] ? intl.formatMessage(messages[value]) : value

const options = (intl: IntlShape, messages: Enum) =>
  Object.keys(messages).map((value) => ({value, label: intl.formatMessage(messages[value])}))

export const stepKeyLabel = (intl: IntlShape, value: string) => label(intl, STEP_KEY, value)
export const documentTypeLabel = (intl: IntlShape, value: string) =>
  label(intl, DOCUMENT_TYPE, value)
export const documentContextLabel = (intl: IntlShape, value: string) =>
  label(intl, DOCUMENT_CONTEXT, value)

export const stepKeyOptions = (intl: IntlShape) => options(intl, STEP_KEY)
export const documentTypeOptions = (intl: IntlShape) => options(intl, DOCUMENT_TYPE)
export const documentContextOptions = (intl: IntlShape) => options(intl, DOCUMENT_CONTEXT)
